import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { fmtC, fmtH } from "@/lib/calc";
import { Plus, Trash2, ArrowUpRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import SalesforceAutocomplete from "@/components/SalesforceAutocomplete";

const STAGES = [
  { id: "lead", label: "Lead", color: "bg-zinc-400" },
  { id: "estimating", label: "Estimating", color: "bg-[#002FA7]" },
  { id: "submitted", label: "Submitted", color: "bg-amber-500" },
  { id: "won", label: "Won", color: "bg-emerald-600" },
  { id: "lost", label: "Lost", color: "bg-red-600" },
];

export default function PipelinePage() {
  const [opps, setOpps] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", client: "", description: "", account_id: "", sf_opportunity_id: "" });
  const [dragOver, setDragOver] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null); // { stage, idx }
  const navigate = useNavigate();

  const load = () => api.get("/opportunities").then((r) => setOpps(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps -- fetch on mount only

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/opportunities", form);
      setOpen(false);
      setForm({ name: "", client: "", description: "", account_id: "", sf_opportunity_id: "" });
      navigate(`/opportunity/${data.id}`);
    } catch (err) { toast.error(apiError(err)); }
  };

  const setStage = async (opp, stage) => {
    setOpps(opps.map((o) => (o.id === opp.id ? { ...o, stage } : o)));
    try { await api.put(`/opportunities/${opp.id}`, { stage }); } catch (e) { toast.error(apiError(e)); load(); }
  };

  const remove = async (opp) => {
    if (!window.confirm(`Delete opportunity "${opp.name}" and its estimation?`)) return;
    try { await api.delete(`/opportunities/${opp.id}`); setOpps(opps.filter((o) => o.id !== opp.id)); }
    catch (e) { toast.error(apiError(e)); }
  };

  const onDrop = (e, stageId, dropIndex = null) => {
    e.preventDefault();
    setDragOver(null);
    setDragOverIdx(null);
    const id = e.dataTransfer.getData("text/plain");
    const opp = opps.find((o) => o.id === id);
    if (!opp) return;

    // Compute reordered list per-stage.
    const stageOpps = opps.filter((o) => o.stage === stageId && o.id !== id);
    const insertAt = dropIndex == null ? stageOpps.length : Math.min(dropIndex, stageOpps.length);
    const reordered = [...stageOpps.slice(0, insertAt), { ...opp, stage: stageId }, ...stageOpps.slice(insertAt)];

    // Build a new full opps list with positions updated for the affected stage(s).
    const otherStageOpps = opps.filter((o) => o.stage !== stageId && o.id !== id);
    const next = [
      ...otherStageOpps,
      ...reordered.map((o, idx) => ({ ...o, stage: stageId, position: idx })),
    ];
    setOpps(next);

    // Persist: send positions for the affected stage (plus the moved card's old stage if different).
    const payload = reordered.map((o, idx) => ({ id: o.id, stage: stageId, position: idx }));
    api.put("/opportunities/reorder", payload).catch((err) => { toast.error(apiError(err)); load(); });
  };

  const inputCls = "w-full border border-zinc-300 bg-white px-3 py-2 text-sm rounded-sm focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none";

  // Portfolio aggregates — Active = everything except Lost.
  const activeOpps = (opps || []).filter((o) => o.stage !== "lost");
  const wonOpps = (opps || []).filter((o) => o.stage === "won");
  const sum = (rows, fn) => rows.reduce((a, r) => a + (Number(fn(r)) || 0), 0);
  const totalRevenue = sum(activeOpps, (o) => o.summary?.final_price);
  const wonRevenue = sum(wonOpps, (o) => o.summary?.final_price);
  const totalSubtotal = sum(activeOpps, (o) => o.summary?.subtotal);
  const totalMarginAmt = sum(activeOpps, (o) => o.summary?.margin_amount);
  const avgMargin = totalSubtotal > 0 ? totalMarginAmt / totalSubtotal : 0;
  const totalHrs = sum(activeOpps, (o) => o.summary?.total_hours);
  const onshoreHrs = sum(activeOpps, (o) => o.delivery_mix?.onshore_hours);
  const offshoreHrs = sum(activeOpps, (o) => o.delivery_mix?.offshore_hours);
  const mixTotal = onshoreHrs + offshoreHrs;
  const onPct = mixTotal > 0 ? onshoreHrs / mixTotal : 0;
  const offPct = mixTotal > 0 ? offshoreHrs / mixTotal : 0;

  return (
    <Layout>
      <div className="p-6 md:p-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-zinc-500 mb-1">Opportunity Pipeline</p>
            <h1 className="font-heading text-3xl font-medium tracking-tighter">Opportunities</h1>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="new-opportunity-button" className="flex items-center gap-1.5 bg-[#002FA7] hover:bg-[#002280] text-white text-sm font-medium px-4 py-2 rounded-sm transition-colors">
                <Plus size={15} strokeWidth={2} /> New opportunity
              </button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading tracking-tight">New opportunity</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-4 pt-2">
                <div>
                  <label className="text-xs font-medium text-zinc-700 mb-1 block flex items-center justify-between">
                    <span>Client</span>
                    <span className="text-[10px] uppercase tracking-wider text-zinc-400">Salesforce Account</span>
                  </label>
                  <SalesforceAutocomplete
                    endpoint="/salesforce/accounts/search"
                    value={form.client}
                    onChange={(v) => setForm((f) => ({
                      ...f,
                      client: v,
                      // typing manually clears the account binding (and downstream opp)
                      account_id: "",
                      name: f.account_id ? "" : f.name,
                    }))}
                    onPick={(item) => setForm((f) => ({
                      ...f,
                      client: item.name,
                      account_id: item.id,
                      name: "", // reset opportunity when client changes
                    }))}
                    placeholder="Type to search Salesforce accounts…"
                    testId="opp-client-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-zinc-700 mb-1 block flex items-center justify-between">
                    <span>Opportunity</span>
                    <span className="text-[10px] uppercase tracking-wider text-zinc-400">
                      {form.account_id ? "Filtered by account" : "Salesforce"}
                    </span>
                  </label>
                  <SalesforceAutocomplete
                    endpoint="/salesforce/opportunities/search"
                    value={form.name}
                    onChange={(v) => setForm((f) => ({ ...f, name: v, sf_opportunity_id: "" }))}
                    onPick={(item) => setForm((f) => ({
                      ...f,
                      name: item.name,
                      sf_opportunity_id: item.id,
                      // if client wasn't picked yet, fill it from the opp
                      client: f.client || item.client || f.client,
                    }))}
                    placeholder={form.account_id
                      ? "Focus to see this account's opportunities, or type to filter…"
                      : "Type to search Salesforce opportunities…"}
                    testId="opp-name-input"
                    extraParams={form.account_id ? { account_id: form.account_id } : null}
                    minChars={form.account_id ? 0 : 2}
                    renderItem={(item) => (
                      <>
                        <p className="text-sm">{item.name}</p>
                        {item.client && <p className="text-[11px] text-zinc-500 mt-0.5">{item.client}</p>}
                      </>
                    )}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-zinc-700 mb-1 block">Description</label>
                  <textarea data-testid="opp-description-input" rows={3} className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </div>
                <button data-testid="opp-create-submit" className="w-full bg-[#002FA7] hover:bg-[#002280] text-white text-sm font-medium py-2.5 rounded-sm">Create</button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {!opps ? (
          <p className="text-sm text-zinc-500">Loading…</p>
        ) : (
          <>
            <div data-testid="pipeline-summary-strip"
              className="mb-8 bg-zinc-900 text-white rounded-sm border border-zinc-900 px-6 py-5 grid grid-cols-2 md:grid-cols-5 gap-6">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-1.5">Active opportunities</p>
                <p data-testid="strip-active-count" className="font-heading text-2xl tracking-tighter">{activeOpps.length}</p>
                <p className="text-[11px] text-zinc-500 mt-0.5">{wonOpps.length} won · {opps.length} total</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-1.5">Pipeline revenue</p>
                <p data-testid="strip-pipeline-revenue" className="font-heading text-2xl tracking-tighter">{fmtC(totalRevenue)}</p>
                <p className="text-[11px] text-zinc-500 mt-0.5">{fmtC(wonRevenue)} won</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-1.5">Avg margin</p>
                <p data-testid="strip-blended-margin" className="font-heading text-2xl tracking-tighter">{Math.round(avgMargin * 100)}%</p>
                <p className="text-[11px] text-zinc-500 mt-0.5">{fmtC(totalMarginAmt)} margin amt</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-1.5">Total hours</p>
                <p data-testid="strip-total-hours" className="font-heading text-2xl tracking-tighter">{fmtH(totalHrs)}</p>
                <p className="text-[11px] text-zinc-500 mt-0.5">incl. contingency</p>
              </div>
              <div data-testid="strip-delivery-mix">
                <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-1.5">Delivery mix</p>
                <p className="font-heading text-2xl tracking-tighter">
                  <span className="text-[#7AA0FF]">{Math.round(onPct * 100)}%</span>
                  <span className="text-zinc-600 mx-1.5 text-base">/</span>
                  <span className="text-emerald-400">{Math.round(offPct * 100)}%</span>
                </p>
                <div className="flex h-1 w-full overflow-hidden rounded-full bg-zinc-700 mt-2">
                  <div className="bg-[#7AA0FF]" style={{ width: `${onPct * 100}%` }} />
                  <div className="bg-emerald-400" style={{ width: `${offPct * 100}%` }} />
                </div>
                <p className="text-[11px] text-zinc-500 mt-1.5">onshore / offshore</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4">
            {STAGES.map((stage) => {
              const items = opps.filter((o) => o.stage === stage.id);
              return (
                <div key={stage.id} data-testid={`stage-column-${stage.id}`}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(stage.id); }}
                  onDragLeave={(e) => {
                    // Only clear if leaving the column entirely (not just moving to a child)
                    if (e.currentTarget.contains(e.relatedTarget)) return;
                    setDragOver((d) => (d === stage.id ? null : d));
                    setDragOverIdx((di) => (di?.stage === stage.id ? null : di));
                  }}
                  onDrop={(e) => onDrop(e, stage.id, dragOverIdx?.stage === stage.id ? dragOverIdx.idx : null)}
                  className={`min-h-[200px] rounded-sm transition-colors ${dragOver === stage.id ? "bg-blue-50/70 outline outline-1 outline-dashed outline-[#002FA7]/40" : ""}`}>
                  <div className="flex items-center gap-2 mb-3 px-1">
                    <span className={`w-2 h-2 rounded-full ${stage.color}`} />
                    <span className="text-[10px] uppercase tracking-[0.2em] font-medium text-zinc-500">{stage.label}</span>
                    <span className="text-[10px] font-mono text-zinc-400 ml-auto">{items.length}</span>
                  </div>
                  <div className="space-y-3">
                    {items.map((o, idx) => (
                      <div key={o.id}>
                        {dragOverIdx?.stage === stage.id && dragOverIdx?.idx === idx && (
                          <div data-testid={`drop-indicator-${stage.id}-${idx}`}
                            className="h-1 -mt-1 mb-2 rounded-full bg-[#002FA7]" />
                        )}
                        <div data-testid={`opportunity-card-${o.id}`}
                          draggable
                          onDragStart={(e) => { e.dataTransfer.setData("text/plain", o.id); e.dataTransfer.effectAllowed = "move"; }}
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const rect = e.currentTarget.getBoundingClientRect();
                            const above = e.clientY < rect.top + rect.height / 2;
                            setDragOver(stage.id);
                            setDragOverIdx({ stage: stage.id, idx: above ? idx : idx + 1 });
                          }}
                          onDrop={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const above = e.clientY < rect.top + rect.height / 2;
                            onDrop(e, stage.id, above ? idx : idx + 1);
                            e.stopPropagation();
                          }}
                          className="bg-white border border-zinc-200 rounded-sm p-4 hover:shadow-[0_4px_12px_rgba(0,0,0,0.05)] transition-shadow group cursor-grab active:cursor-grabbing">
                        <div className="flex items-start justify-between gap-2">
                          <button data-testid={`open-opportunity-${o.id}`} onClick={() => navigate(`/opportunity/${o.id}`)} className="text-left">
                            <p className="text-sm font-medium leading-snug hover:text-[#002FA7]">{o.name}</p>
                            <p className="text-xs text-zinc-500 mt-0.5">{o.client || "—"}</p>
                          </button>
                          <ArrowUpRight size={14} className="text-zinc-300 group-hover:text-[#002FA7] shrink-0 mt-0.5" strokeWidth={1.5} />
                        </div>
                        {o.summary && o.summary.total_hours > 0 && (
                          <div className="mt-3 pt-3 border-t border-zinc-100 grid grid-cols-2 gap-1">
                            <div><p className="text-[10px] text-zinc-400 uppercase tracking-wide">Hours</p><p className="text-xs font-mono">{fmtH(o.summary.total_hours)}</p></div>
                            <div><p className="text-[10px] text-zinc-400 uppercase tracking-wide">Price</p><p className="text-xs font-mono font-medium">{fmtC(o.summary.final_price)}</p></div>
                          </div>
                        )}
                        {o.delivery_mix && o.delivery_mix.total_hours > 0 && (
                          <div data-testid={`delivery-mix-${o.id}`} className="mt-2.5">
                            <p className="text-[10px] uppercase tracking-wide text-zinc-400 mb-1">Delivery mix</p>
                            <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-zinc-100">
                              <div className="bg-[#002FA7]" style={{ width: `${o.delivery_mix.onshore_pct * 100}%` }} title={`Onshore ${Math.round(o.delivery_mix.onshore_pct * 100)}%`} />
                              <div className="bg-emerald-500" style={{ width: `${o.delivery_mix.offshore_pct * 100}%` }} title={`Offshore ${Math.round(o.delivery_mix.offshore_pct * 100)}%`} />
                            </div>
                            <div className="flex items-center gap-3 mt-1.5 text-[10px]">
                              <span className="flex items-center gap-1">
                                <span className="w-2 h-2 rounded-sm bg-[#002FA7]" />
                                <span className="text-zinc-500">Onshore</span>
                                <span className="font-mono font-medium text-zinc-700">{Math.round(o.delivery_mix.onshore_pct * 100)}%</span>
                              </span>
                              <span className="flex items-center gap-1">
                                <span className="w-2 h-2 rounded-sm bg-emerald-500" />
                                <span className="text-zinc-500">Offshore</span>
                                <span className="font-mono font-medium text-zinc-700">{Math.round(o.delivery_mix.offshore_pct * 100)}%</span>
                              </span>
                            </div>
                          </div>
                        )}
                        <div className="mt-3 flex items-center justify-between">
                          <select data-testid={`stage-select-${o.id}`} value={o.stage} onChange={(e) => setStage(o, e.target.value)}
                            className="text-[11px] border border-zinc-200 rounded-sm px-1.5 py-1 bg-zinc-50 text-zinc-600 outline-none focus:border-[#002FA7]">
                            {STAGES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                          </select>
                          <button data-testid={`delete-opportunity-${o.id}`} onClick={() => remove(o)} className="text-zinc-300 hover:text-red-600">
                            <Trash2 size={13} strokeWidth={1.5} />
                          </button>
                        </div>
                      </div>
                    </div>
                    ))}
                    {dragOverIdx?.stage === stage.id && dragOverIdx?.idx === items.length && (
                      <div data-testid={`drop-indicator-${stage.id}-end`} className="h-1 rounded-full bg-[#002FA7]" />
                    )}
                  </div>
                </div>
              );
            })}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
